/**
 * OpportunityDetail Component
 * Full detail page for a single opportunity
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    ListGroup,
} from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';

import SEO from '@/components/common/SEO';
import { useOpportunityApi } from '@/shared/services/opportunityApi';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useCapability from '@/shared/hooks/useCapability';

import { getTypeColor } from '../utils/opportunityColors';

import '../Opportunities.css';

// Google Maps requires a JS style object for mapContainerStyle
const SMALL_MAP_STYLE = {
    width: '100%',
    height: '200px',
    borderRadius: '8px',
};

function formatDate(dateString) {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatTime(timeStr) {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
}

function formatAmount(amount) {
    if (!amount) return null;
    const { min, max } = amount;
    if (min && max) {
        if (min === max) return `$${min.toLocaleString()}`;
        return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    if (max) return `Up to $${max.toLocaleString()}`;
    if (min) return `From $${min.toLocaleString()}`;
    return null;
}

export default function OpportunityDetail() {
    const { id } = useParams();
    const opportunityApi = useOpportunityApi();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'RILE Connect';
    // Opportunity partnerships ("seeking collaboration partners") are a
    // per-tenant capability. When the active ecosystem has disabled
    // `opportunityPartnerships`, hide the seeking-partners badge and the
    // collaboration-partners section even if an opportunity carries the flag.
    const partnershipsEnabled = useCapability('opportunityPartnerships');

    const [opportunity, setOpportunity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    });

    useEffect(() => {
        const loadOpportunity = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await opportunityApi.fetchOpportunity(id);
                setOpportunity(data);
            } catch (err) {
                console.error('Failed to load opportunity:', err);
                setError('Opportunity not found or failed to load.');
            } finally {
                setLoading(false);
            }
        };

        loadOpportunity();
    }, [id, opportunityApi]);

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error || !opportunity) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    {error || 'Opportunity not found.'}
                    <div className="mt-3">
                        <Link
                            to="/opportunities"
                            className="btn btn-outline-primary"
                        >
                            Back to Opportunities
                        </Link>
                    </div>
                </Alert>
            </Container>
        );
    }

    const {
        title,
        description,
        type,
        organization,
        location,
        dates,
        event,
        funding,
        compensation,
        application,
        targetAudience,
        requirements,
        skillsDeveloped,
        contact,
        regions,
        commitment,
    } = opportunity;

    const hasLocation =
        location?.coordinates?.coordinates &&
        location.coordinates.coordinates.length === 2;

    return (
        <>
            <SEO
                title={title}
                description={
                    description?.substring(0, 160) ||
                    `${title}, a STEM opportunity on ${ecosystemName}.`
                }
                canonical={`/opportunities/${id}`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Opportunities', path: '/opportunities' },
                    { name: title, path: `/opportunities/${id}` },
                ]}
            />

            {/* Header */}
            <div className="opportunity-detail-header">
                <Container>
                    <Link
                        to="/opportunities"
                        className="text-white text-decoration-none mb-3 d-inline-block"
                    >
                        ← Back to Opportunities
                    </Link>
                    <h1 className="mb-2">{title}</h1>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <Badge
                            bg=""
                            className="type-color-badge fs-6"
                            /* CSS custom property for dynamic type color -- must be inline */
                            style={{ '--type-color': getTypeColor(type) }}
                        >
                            {type}
                        </Badge>
                        {location?.isRemote && (
                            <Badge bg="info" className="fs-6">
                                🌐 Remote
                            </Badge>
                        )}
                        {compensation?.isPaid && (
                            <Badge bg="success" className="fs-6">
                                💵 Paid
                            </Badge>
                        )}
                        {partnershipsEnabled && funding?.seekingPartners && (
                            <Badge bg="warning" text="dark" className="fs-6">
                                🤝 Seeking Partners
                            </Badge>
                        )}
                    </div>
                    {organization && (
                        <Link
                            to={`/organizations/${organization._id}`}
                            className="text-white text-decoration-none"
                        >
                            <strong>{organization.name}</strong>
                        </Link>
                    )}
                </Container>
            </div>

            <Container className="py-4">
                <Row>
                    {/* Main Content */}
                    <Col lg={8}>
                        {/* Description */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body>
                                <h5 className="text-primary mb-3">
                                    About This Opportunity
                                </h5>
                                <div className="opportunity-description">
                                    {description}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Requirements */}
                        {requirements && requirements.length > 0 && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <h5 className="text-primary mb-3">
                                        Requirements
                                    </h5>
                                    <ul className="mb-0">
                                        {requirements.map((req, i) => (
                                            <li key={i}>{req}</li>
                                        ))}
                                    </ul>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Skills Developed */}
                        {skillsDeveloped && skillsDeveloped.length > 0 && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <h5 className="text-primary mb-3">
                                        Skills You&apos;ll Develop
                                    </h5>
                                    <div className="d-flex flex-wrap gap-2">
                                        {skillsDeveloped.map((skill, i) => (
                                            <Badge
                                                key={i}
                                                bg="light"
                                                text="dark"
                                                className="py-2 px-3"
                                            >
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Funding Details */}
                        {funding?.isFunding && (
                            <Card className="border-0 shadow-sm mb-4 funding-card">
                                <Card.Body>
                                    <h5 className="text-success mb-3">
                                        💰 Funding Details
                                    </h5>
                                    <ListGroup variant="flush">
                                        {funding.amount && (
                                            <ListGroup.Item className="px-0">
                                                <strong>Amount:</strong>{' '}
                                                <span className="funding-amount">
                                                    {formatAmount(
                                                        funding.amount
                                                    )}
                                                </span>
                                            </ListGroup.Item>
                                        )}
                                        {funding.funder && (
                                            <ListGroup.Item className="px-0">
                                                <strong>Funder:</strong>{' '}
                                                {funding.funder}
                                            </ListGroup.Item>
                                        )}
                                        {funding.awardCycle && (
                                            <ListGroup.Item className="px-0">
                                                <strong>Award Cycle:</strong>{' '}
                                                {funding.awardCycle}
                                            </ListGroup.Item>
                                        )}
                                        {funding.fundingDuration && (
                                            <ListGroup.Item className="px-0">
                                                <strong>Duration:</strong>{' '}
                                                {funding.fundingDuration}
                                            </ListGroup.Item>
                                        )}
                                        {funding.matchRequired && (
                                            <ListGroup.Item className="px-0">
                                                <Badge bg="warning" text="dark">
                                                    Match Required
                                                </Badge>
                                                {funding.matchDetails && (
                                                    <span className="ms-2">
                                                        {funding.matchDetails}
                                                    </span>
                                                )}
                                            </ListGroup.Item>
                                        )}
                                        {funding.eligibleActivities?.length >
                                            0 && (
                                            <ListGroup.Item className="px-0">
                                                <strong>Eligible Uses:</strong>
                                                <ul className="mb-0 mt-1">
                                                    {funding.eligibleActivities.map(
                                                        (activity, i) => (
                                                            <li key={i}>
                                                                {activity}
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            </ListGroup.Item>
                                        )}
                                    </ListGroup>

                                    {partnershipsEnabled &&
                                        funding.seekingPartners && (
                                            <Alert
                                                variant="info"
                                                className="mt-3 mb-0"
                                            >
                                                <strong>
                                                    🤝 Seeking Collaboration
                                                    Partners
                                                </strong>
                                                {funding.partnerRequirements && (
                                                    <p className="mb-0 mt-2">
                                                        {
                                                            funding.partnerRequirements
                                                        }
                                                    </p>
                                                )}
                                                {funding.partnerContact
                                                    ?.email && (
                                                    <p className="mb-0 mt-2">
                                                        <strong>
                                                            Contact:
                                                        </strong>{' '}
                                                        <a
                                                            href={`mailto:${funding.partnerContact.email}`}
                                                        >
                                                            {funding
                                                                .partnerContact
                                                                .name ||
                                                                funding
                                                                    .partnerContact
                                                                    .email}
                                                        </a>
                                                    </p>
                                                )}
                                            </Alert>
                                        )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Organization Info */}
                        {organization && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <h5 className="text-primary mb-3">
                                        About the Organization
                                    </h5>
                                    <div className="d-flex gap-3">
                                        {organization.imageUrl && (
                                            <img
                                                src={organization.imageUrl}
                                                alt={organization.name}
                                                className="rounded org-thumbnail-lg"
                                            />
                                        )}
                                        <div>
                                            <h6>{organization.name}</h6>
                                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                <Badge bg="secondary">
                                                    {organization.primarySector}
                                                </Badge>
                                                <Link
                                                    to={`/organizations/${organization._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    View Organization
                                                </Link>
                                            </div>
                                            {organization.description && (
                                                <p className="small text-muted mb-0">
                                                    {organization.description.substring(
                                                        0,
                                                        200
                                                    )}
                                                    ...
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        <div className="opportunity-sidebar">
                            {/* Apply Button */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    {application?.url ? (
                                        <Button
                                            href={application.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="primary"
                                            className="apply-button"
                                        >
                                            Apply Now
                                        </Button>
                                    ) : application?.email ? (
                                        <Button
                                            href={`mailto:${application.email}`}
                                            variant="primary"
                                            className="apply-button"
                                        >
                                            Apply via Email
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="secondary"
                                            className="apply-button"
                                            disabled
                                        >
                                            Contact for Details
                                        </Button>
                                    )}

                                    {application?.deadline && (
                                        <p className="text-center mt-3 mb-0 text-muted small">
                                            <strong>Deadline:</strong>{' '}
                                            {formatDate(application.deadline)}
                                        </p>
                                    )}
                                    {application?.isRolling && (
                                        <p className="text-center mt-2 mb-0">
                                            <Badge bg="info">
                                                Rolling Applications
                                            </Badge>
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Key Details */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white">
                                    <strong>Details</strong>
                                </Card.Header>
                                <ListGroup variant="flush">
                                    {dates?.startDate && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                Start Date
                                            </small>
                                            {formatDate(dates.startDate)}
                                            {event?.startTime && (
                                                <span className="ms-2 text-muted">
                                                    {formatTime(
                                                        event.startTime
                                                    )}
                                                </span>
                                            )}
                                        </ListGroup.Item>
                                    )}
                                    {dates?.endDate && !dates?.isOngoing && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                End Date
                                            </small>
                                            {formatDate(dates.endDate)}
                                        </ListGroup.Item>
                                    )}
                                    {commitment?.type && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                Time Commitment
                                            </small>
                                            {commitment.type}
                                            {commitment.hoursPerWeek && (
                                                <span>
                                                    {' '}
                                                    ({
                                                        commitment.hoursPerWeek
                                                    }{' '}
                                                    hrs/week)
                                                </span>
                                            )}
                                        </ListGroup.Item>
                                    )}
                                    {compensation?.isPaid && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                Compensation
                                            </small>
                                            <Badge bg="success">Paid</Badge>
                                            {compensation.details && (
                                                <span className="ms-2">
                                                    {compensation.details}
                                                </span>
                                            )}
                                        </ListGroup.Item>
                                    )}
                                    {targetAudience?.length > 0 && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                Who It&apos;s For
                                            </small>
                                            {targetAudience.join(', ')}
                                        </ListGroup.Item>
                                    )}
                                    {regions?.length > 0 && (
                                        <ListGroup.Item>
                                            <small className="text-muted d-block">
                                                Regions
                                            </small>
                                            {regions.join(', ')}
                                        </ListGroup.Item>
                                    )}
                                </ListGroup>
                            </Card>

                            {/* Location Map */}
                            {hasLocation && isLoaded && (
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-white">
                                        <strong>📍 Location</strong>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <GoogleMap
                                            mapContainerStyle={SMALL_MAP_STYLE}
                                            center={{
                                                lat: location.coordinates
                                                    .coordinates[1],
                                                lng: location.coordinates
                                                    .coordinates[0],
                                            }}
                                            zoom={14}
                                            options={{
                                                disableDefaultUI: true,
                                                zoomControl: true,
                                                gestureHandling: 'cooperative',
                                            }}
                                        >
                                            <MarkerF
                                                position={{
                                                    lat: location.coordinates
                                                        .coordinates[1],
                                                    lng: location.coordinates
                                                        .coordinates[0],
                                                }}
                                                title={title}
                                            />
                                        </GoogleMap>
                                    </Card.Body>
                                    <Card.Footer className="bg-white small">
                                        {location.venueName && (
                                            <strong>
                                                {location.venueName}
                                            </strong>
                                        )}
                                        {location.address && (
                                            <div>{location.address}</div>
                                        )}
                                        {location.city && (
                                            <div>
                                                {location.city}
                                                {location.state &&
                                                    `, ${location.state}`}{' '}
                                                {location.zipCode}
                                            </div>
                                        )}
                                        <Button
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${location.coordinates.coordinates[1]},${location.coordinates.coordinates[0]}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="outline-primary"
                                            size="sm"
                                            className="w-100 mt-2"
                                        >
                                            🧭 Get Directions
                                        </Button>
                                    </Card.Footer>
                                </Card>
                            )}

                            {/* Contact */}
                            {contact && (contact.name || contact.email) && (
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-white">
                                        <strong>Contact</strong>
                                    </Card.Header>
                                    <Card.Body>
                                        {contact.name && (
                                            <p className="mb-1">
                                                {contact.name}
                                            </p>
                                        )}
                                        {contact.email && (
                                            <p className="mb-1">
                                                <a
                                                    href={`mailto:${contact.email}`}
                                                >
                                                    {contact.email}
                                                </a>
                                            </p>
                                        )}
                                        {contact.phone && (
                                            <p className="mb-0">
                                                <a
                                                    href={`tel:${contact.phone}`}
                                                >
                                                    {contact.phone}
                                                </a>
                                            </p>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
